import React from 'react';

import { useLocalizedStringFormatter } from '@coursera/cds-common';

import i18nMessages from '@core/table/i18n';
import type {
  TableCommonDefaultProps,
  SortDirection,
  TablePagination,
  TableContextProps,
  Sort,
  Column,
} from '@core/table/types';
import { useControlled } from '@core/utils';

import { useA11yAnnouncer } from './A11yAnnouncer';

const DEFAULT_INITIAL_SORT_DIRECTION: SortDirection = 'ascending';
const DEFAULT_INITIAL_PAGE = 1;
const DEFAULT_INITIAL_PAGE_SIZE = 5;
const DEFAULT_INITIAL_PAGE_SIZE_INPUT_OPTIONS = [
  { label: '5', value: 5 },
  { label: '10', value: 10 },
  { label: '20', value: 20 },
  { label: '100', value: 100 },
];

const TableContext = React.createContext<TableContextProps<any, string>>({
  sort: {
    direction: DEFAULT_INITIAL_SORT_DIRECTION,
    key: undefined,
  },
  pagination: {
    page: DEFAULT_INITIAL_PAGE,
    pageSize: DEFAULT_INITIAL_PAGE_SIZE,
    totalRecords: 0,
    setPage: undefined,
    setPageSize: undefined,
    setTotalRecords: undefined,
    pageSizeInputOptions: DEFAULT_INITIAL_PAGE_SIZE_INPUT_OPTIONS,
    hidePagination: false,
  },
  rowHeaderAccessor: undefined,
  handleSortClick: undefined,
  id: undefined,
  columns: [],
  status: undefined,
  cellVerticalAlign: undefined,
  serverMode: false,
});

export const useTableContext = () => React.useContext(TableContext);

export const usePrivateTableContext = <
  T extends Record<string, any>,
  K extends keyof T & string
>(
  props: TableCommonDefaultProps<T, K>
) => {
  const {
    defaultSortDirection = DEFAULT_INITIAL_SORT_DIRECTION,
    defaultSortKey,
    defaultPage = DEFAULT_INITIAL_PAGE,
    defaultPageSize = DEFAULT_INITIAL_PAGE_SIZE,
    totalRows,
    pageSizeInputOptions,
    hidePagination,
    rowHeaderAccessor,
    cellVerticalAlign = 'middle',
    id,
    columns,
    status = 'idle',
    serverMode,
    onPageChange,
    onPageSizeChange,
    onSort,
    page: pageFromProps,
    pageSize: pageSizeFromProps,
    sortKey: sortKeyFromProps,
    sortDirection: sortDirectionFromProps,
  } = props;

  const [sortKey, setSortKey] = useControlled({
    controlled: sortKeyFromProps,
    default: defaultSortKey,
    name: 'usePrivateTableContext',
    state: 'sortKey',
  });

  const [sortDirection, setSortDirection] = useControlled({
    controlled: sortDirectionFromProps,
    default: defaultSortDirection,
    name: 'usePrivateTableContext',
    state: 'sortDirection',
  });

  const [page, setCurrentPage] = useControlled({
    controlled: pageFromProps,
    default: defaultPage,
    name: 'usePrivateTableContext',
    state: 'page',
  });

  const [pageSize, setPageSize] = useControlled({
    controlled: pageSizeFromProps,
    default: defaultPageSize,
    name: 'usePrivateTableContext',
    state: 'pageSize',
  });

  const [totalRecords, setTotalRecords] = React.useState(totalRows);

  const { addAnnouncement } = useA11yAnnouncer();
  const stringFormatter = useLocalizedStringFormatter(i18nMessages);

  React.useEffect(() => {
    setTotalRecords(totalRows);
  }, [totalRows]);

  React.useEffect(() => {
    let loadingA11yMsg = null;
    if (status === 'loading') {
      loadingA11yMsg = stringFormatter.format('loading_table');
    }
    if (status === 'idle') {
      loadingA11yMsg = stringFormatter.format('loading_complete');
    }
    if (loadingA11yMsg && addAnnouncement) {
      addAnnouncement(loadingA11yMsg);
    }
  }, [status]);

  const calculateNewSortDirection = React.useCallback(
    (newSortKey: string): SortDirection => {
      let newSortDircetion: SortDirection;
      // Only Sort direction is changed
      if (sortKey === newSortKey) {
        newSortDircetion =
          sortDirection === 'ascending' ? 'descending' : 'ascending';
      } else {
        newSortDircetion = 'ascending';
      }

      return newSortDircetion;
    },
    [sortKey, sortDirection]
  );

  const calculateA11yMessage = React.useCallback<
    (newSortKey: K, newSortDirection: SortDirection) => string
  >(
    (newSortKey, newSortDirection): string => {
      const sortedColumn = columns.find(
        (column) => column.accessor === newSortKey
      ) as NonNullable<Column<T, K>>;

      const sortColumnLabel = (sortedColumn['aria-label'] ||
        sortedColumn.label) as NonNullable<string>;

      const localizedAscending = stringFormatter.format('ascending');
      const localizedDescending = stringFormatter.format('descending');

      const sortOrder =
        newSortDirection === 'ascending'
          ? localizedAscending
          : localizedDescending;

      return stringFormatter.format('table_sorted', {
        sortColumnLabel,
        sortOrder,
      });
    },
    [sortKey, sortDirection]
  );

  const handleSortClick = React.useCallback(
    (newSortKey: K) => {
      const newSortDirection = calculateNewSortDirection(newSortKey);

      setSortKey(newSortKey);
      setSortDirection(newSortDirection);

      const a11yMessage = calculateA11yMessage(newSortKey, newSortDirection);
      if (a11yMessage && addAnnouncement) {
        addAnnouncement(a11yMessage);
      }
      onSort?.(newSortKey, newSortDirection);
    },
    [calculateNewSortDirection, addAnnouncement, calculateA11yMessage]
  );

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    newPage: number
  ) => {
    onPageChange?.(event, newPage);
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (
    event: React.ChangeEvent<unknown>,
    newPageSize: number
  ) => {
    onPageSizeChange?.(event, newPageSize);
    setPageSize(newPageSize);
  };

  const sort: Sort = {
    direction: sortDirection,
    key: sortKey,
  };

  const pagination: TablePagination = {
    page,
    pageSize,
    totalRecords,
    setPage: handlePageChange,
    setPageSize: handlePageSizeChange,
    setTotalRecords,
    pageSizeInputOptions,
    hidePagination:
      hidePagination ||
      totalRecords === 0 ||
      !['idle', 'progressing'].includes(status),
  };

  return {
    sort,
    handleSortClick,
    pagination,
    rowHeaderAccessor,
    id,
    columns,
    status,
    cellVerticalAlign,
    serverMode,
  };
};

type Props = {
  children?: React.ReactNode;
} & TableCommonDefaultProps<any, any>;

export const TableContextProvider = ({ children, ...props }: Props) => {
  const contextProviderValue = usePrivateTableContext(props);

  return (
    <TableContext.Provider value={contextProviderValue}>
      {children}
    </TableContext.Provider>
  );
};
